import React, { useState } from "react";
import { db } from "../../firebase"; // ตรวจสอบ path ให้ตรงกับโปรเจกต์คุณ
import { updateDoc, doc } from "firebase/firestore";

const ContactsTab = ({ trip, theme, onUpdateTrip, isDarkMode }) => {
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  // 🌟 1. ตั้งค่าหมวดหมู่เริ่มต้น
  const defaultCategories = [
    {
      id: "emergency",
      label: "ฉุกเฉิน / โรงพยาบาล",
      icon: "🚨",
      color: "#e74c3c",
    },
    { id: "embassy", label: "สถานทูต / กงสุล", icon: "🏛️", color: "#f39c12" },
    { id: "airline", label: "สายการบิน", icon: "✈️", color: "#3498db" },
    { id: "hotel", label: "ที่พัก", icon: "🏨", color: "#9b59b6" },
    { id: "transport", label: "รถเช่า / ขนส่ง", icon: "🚕", color: "#1abc9c" },
    { id: "other", label: "อื่นๆ", icon: "📌", color: "#7f8c8d" },
  ];

  // 🌟 2. State สำหรับจัดการหมวดหมู่ที่เพิ่มเอง
  const [customCategories, setCustomCategories] = useState(
    trip.customContactCategories || []
  );
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // รวมหมวดหมู่ทั้งหมด (เริ่มต้น + ที่ผู้ใช้สร้าง)
  const allCategories = [...defaultCategories, ...customCategories];

  const initialFormState = {
    title: "",
    category: "emergency",
    phone: "",
    email: "",
    locationName: "",
    mapLink: "",
    otherLink: "",
    note: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  // 🌟 3. ฟังก์ชันบันทึกหมวดหมู่ใหม่
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setIsAddingCategory(false);
      return;
    }

    const newCat = {
      id: `contact_cat_${Date.now()}`,
      label: newCategoryName.trim(),
      icon: "🏷️",
      color: "#a855f7", // สีเริ่มต้นสำหรับหมวดหมู่ที่เพิ่มเอง
    };

    const updatedCategories = [...customCategories, newCat];

    try {
      await updateDoc(doc(db, "trips", trip.id), {
        customContactCategories: updatedCategories,
      });

      trip.customContactCategories = updatedCategories;
      setCustomCategories(updatedCategories);
      setFormData({ ...formData, category: newCat.id }); // เลือกหมวดหมู่นี้อัตโนมัติ

      setNewCategoryName("");
      setIsAddingCategory(false);
    } catch (error) {
      console.error("Error saving custom category:", error);
      alert("เกิดข้อผิดพลาดในการบันทึกหมวดหมู่");
    }
  };

  const handleEditClick = (contact) => {
    setFormData({ ...initialFormState, ...contact });
    setEditingId(contact.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData(initialFormState);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title) return alert("กรุณากรอกชื่อ/หน่วยงาน");

    try {
      const contactData = {
        ...formData,
        id: editingId || `contact_${Date.now()}`,
      };

      let newList;
      if (editingId) {
        newList = (trip.importantContacts || []).map((c) =>
          c.id === editingId ? contactData : c
        );
      } else {
        newList = [...(trip.importantContacts || []), contactData];
      }

      await updateDoc(doc(db, "trips", trip.id), {
        importantContacts: newList,
      });
      onUpdateTrip(newList, "importantContacts");
      handleCancel();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("คุณต้องการลบข้อมูลการติดต่อนี้ใช่หรือไม่?")) {
      const newList = (trip.importantContacts || []).filter((c) => c.id !== id);
      await updateDoc(doc(db, "trips", trip.id), {
        importantContacts: newList,
      });
      onUpdateTrip(newList, "importantContacts");
    }
  };

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
    backgroundColor: isDarkMode ? "#2c2c2c" : "#fff",
    color: isDarkMode ? "#fff" : "#000",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: isDarkMode ? "#aaa" : "#666",
    marginBottom: "5px",
    display: "block",
  };

  const formBgStyle = {
    backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
    borderRadius: "12px",
    padding: "20px",
    border: `1px solid ${theme?.border || (isDarkMode ? "#333" : "#ddd")}`,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    marginBottom: "15px",
  };

  const cardStyle = {
    backgroundColor: theme?.cardBg || (isDarkMode ? "#1e1e1e" : "#fff"),
    borderRadius: "12px",
    padding: "15px 20px",
    border: `1px solid ${theme?.border || "#eee"}`,
    position: "relative",
    marginBottom: "10px",
  };

  // จัดกลุ่มข้อมูลตามหมวดหมู่ทั้งหมด (รวมที่สร้างใหม่ด้วย)
  const groupedContacts = allCategories
    .map((cat) => ({
      ...cat,
      items: (trip.importantContacts || []).filter(
        (c) => c.category === cat.id
      ),
    }))
    .filter((cat) => cat.items.length > 0);

  return (
    <div style={{ paddingBottom: "30px", maxWidth: "800px", margin: "0 auto" }}>
      {/* ================= LIST OF CONTACTS ================= */}
      {!isAdding && !editingId && groupedContacts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {groupedContacts.map((group) => (
            <div key={group.id}>
              <h4
                style={{
                  margin: "0 0 10px 0",
                  color: group.color,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderBottom: `1px dashed ${isDarkMode ? "#444" : "#eee"}`,
                  paddingBottom: "8px",
                }}
              >
                {group.icon} {group.label}
              </h4>

              {group.items.map((contact) => (
                <div key={contact.id} style={cardStyle}>
                  <button
                    onClick={() => handleEditClick(contact)}
                    style={{
                      position: "absolute",
                      top: "15px",
                      right: "15px",
                      background: "none",
                      border: "none",
                      color: "#888",
                      fontSize: "18px",
                      cursor: "pointer",
                    }}
                  >
                    •••
                  </button>

                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: theme?.text,
                      marginBottom: "8px",
                      paddingRight: "30px",
                    }}
                  >
                    {contact.title}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      fontSize: "13px",
                      color: isDarkMode ? "#ccc" : "#555",
                    }}
                  >
                    {contact.phone && (
                      <div>
                        📞 <strong>โทร:</strong>{" "}
                        <a
                          href={`tel:${contact.phone}`}
                          style={{ color: "#3b82f6", textDecoration: "none" }}
                        >
                          {contact.phone}
                        </a>
                      </div>
                    )}
                    {contact.email && (
                      <div>
                        📧 <strong>อีเมล:</strong>{" "}
                        <a
                          href={`mailto:${contact.email}`}
                          style={{ color: "#3b82f6", textDecoration: "none" }}
                        >
                          {contact.email}
                        </a>
                      </div>
                    )}
                    {contact.locationName && (
                      <div>
                        📍 <strong>สถานที่:</strong> {contact.locationName}
                      </div>
                    )}
                    {contact.note && (
                      <div>
                        📝 <strong>หมายเหตุ:</strong> {contact.note}
                      </div>
                    )}
                  </div>

                  {/* Buttons for links */}
                  {(contact.mapLink || contact.otherLink) && (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "12px",
                        paddingTop: "12px",
                        borderTop: `1px solid ${
                          isDarkMode ? "#333" : "#f1f1f1"
                        }`,
                        flexWrap: "wrap",
                      }}
                    >
                      {contact.mapLink && (
                        <a
                          href={contact.mapLink}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            backgroundColor: "rgba(16, 185, 129, 0.1)",
                            color: "#10b981",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          🗺️ เปิด Google Map
                        </a>
                      )}
                      {contact.otherLink && (
                        <a
                          href={contact.otherLink}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            backgroundColor: "rgba(59, 130, 246, 0.1)",
                            color: "#3b82f6",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          🔗 ลิงก์เพิ่มเติม
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ================= FORM (ADD/EDIT) ================= */}
      {(isAdding || editingId) && (
        <div
          style={{
            ...formBgStyle,
            border: isAdding ? `2px dashed #e74c3c` : formBgStyle.border,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "20px",
            }}
          >
            <h4 style={{ margin: 0, color: "#e74c3c" }}>
              {editingId
                ? "✏️ แก้ไขข้อมูลการติดต่อ"
                : "📞 เพิ่มข้อมูลติดต่อใหม่"}
            </h4>
            {editingId && (
              <button
                type="button"
                onClick={() => handleDelete(editingId)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#e74c3c",
                  cursor: "pointer",
                  fontSize: "18px",
                }}
              >
                🗑️
              </button>
            )}
          </div>

          <form onSubmit={handleSave}>
            <div style={{ marginBottom: "15px" }}>
              <label style={labelStyle}>ชื่อหน่วยงาน / บุคคล</label>
              <input
                type="text"
                placeholder="เช่น สถานทูตไทยในโตเกียว, โรงพยาบาล..."
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                style={{ ...inputStyle, fontWeight: "bold" }}
                required
              />
            </div>

            {/* 🌟 4. UI การเลือก/เพิ่มหมวดหมู่แบบ Flex-Wrap (เหมือนหน้าแผนเที่ยว) */}
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>หมวดหมู่</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {allCategories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, category: c.id })}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border:
                        formData.category === c.id
                          ? `1px solid ${c.color}`
                          : `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                      backgroundColor:
                        formData.category === c.id
                          ? isDarkMode
                            ? "#333"
                            : `${c.color}15`
                          : "transparent",
                      color:
                        formData.category === c.id
                          ? c.color
                          : isDarkMode
                          ? "#aaa"
                          : "#666",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      fontFamily: "inherit",
                    }}
                  >
                    {c.icon} {c.label}
                  </button>
                ))}

                {isAddingCategory ? (
                  <div style={{ display: "flex", gap: "4px" }}>
                    <input
                      type="text"
                      placeholder="ชื่อหมวดหมู่..."
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCategory();
                        }
                      }}
                      autoFocus
                      style={{
                        ...inputStyle,
                        padding: "6px 10px",
                        fontSize: "12px",
                        width: "120px",
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "10px",
                        border: "none",
                        background: "#10b981",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      ✓
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingCategory(false);
                        setNewCategoryName("");
                      }}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "10px",
                        border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                        background: "transparent",
                        color: isDarkMode ? "#aaa" : "#666",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsAddingCategory(true)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border: `1px dashed ${isDarkMode ? "#555" : "#bbb"}`,
                      backgroundColor: "transparent",
                      color: isDarkMode ? "#aaa" : "#666",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      fontFamily: "inherit",
                    }}
                  >
                    + เพิ่ม
                  </button>
                )}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "15px",
              }}
            >
              <div>
                <label style={labelStyle}>📞 เบอร์โทรศัพท์</label>
                <input
                  type="text"
                  placeholder="เบอร์ติดต่อ"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>📧 อีเมล</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={labelStyle}>📍 ที่อยู่ / ชื่อสถานที่</label>
              <input
                type="text"
                placeholder="รายละเอียดที่อยู่..."
                value={formData.locationName}
                onChange={(e) =>
                  setFormData({ ...formData, locationName: e.target.value })
                }
                style={inputStyle}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "15px",
              }}
            >
              <div>
                <label style={labelStyle}>🗺️ Google Map Link</label>
                <input
                  type="text"
                  placeholder="วางลิงก์แผนที่..."
                  value={formData.mapLink}
                  onChange={(e) =>
                    setFormData({ ...formData, mapLink: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  🔗 ลิงก์อื่นๆ (เว็บไซต์ / Facebook)
                </label>
                <input
                  type="text"
                  placeholder="วางลิงก์..."
                  value={formData.otherLink}
                  onChange={(e) =>
                    setFormData({ ...formData, otherLink: e.target.value })
                  }
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>📝 หมายเหตุเพิ่มเติม</label>
              <textarea
                placeholder="เช่น เวลาทำการ, สายด่วน..."
                value={formData.note}
                onChange={(e) =>
                  setFormData({ ...formData, note: e.target.value })
                }
                rows="2"
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: `1px solid ${isDarkMode ? "#555" : "#ccc"}`,
                  background: isDarkMode ? "#333" : "white",
                  color: isDarkMode ? "#fff" : "#000",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                style={{
                  padding: "8px 24px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#e74c3c",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                บันทึก
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ปุ่มเพิ่มข้อมูล */}
      {!isAdding && !editingId && (
        <button
          onClick={() => {
            setFormData(initialFormState);
            setIsAdding(true);
          }}
          style={{
            padding: "15px",
            borderRadius: "12px",
            border: `1px dashed ${isDarkMode ? "#555" : "#bbb"}`,
            background: "transparent",
            color: isDarkMode ? "#aaa" : "#666",
            fontSize: "15px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "10px",
            width: "100%",
            justifyContent: "center",
            transition: "0.2s",
          }}
        >
          ➕ เพิ่มข้อมูลการติดต่อ
        </button>
      )}
    </div>
  );
};

export default ContactsTab;
